/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Flex, Input, Text } from 'theme-ui';
import { converter, formatHex, formatRgb, inGamut } from 'culori';
import Button from '@/components/Button';

const COPIED_FEEDBACK_MS = 2000;
const MAX_CHROMA = 0.4;
const GRADIENT_STEPS = 12;
const BOUNDARY_SAMPLES = 100;

type Oklch = { l: number; c: number; h: number };

const formatOklchString = ({ l, c, h }: Oklch): string =>
    `oklch(${(l * 100).toFixed(2)}% ${c.toFixed(3)} ${h.toFixed(2)})`;

// Browsers render oklch() natively (with their own gamut mapping), so sampling raw
// oklch() stops across a channel's range gives an accurate, zero-conversion gradient.
const buildGradient = (current: Oklch, channel: keyof Oklch, min: number, max: number): string => {
    const stops = Array.from({ length: GRADIENT_STEPS + 1 }, (_, i) => {
        const t = min + ((max - min) * i) / GRADIENT_STEPS;
        return formatOklchString({ ...current, [channel]: t });
    });
    return `linear-gradient(to right, ${stops.join(', ')})`;
};

// Positions (0-100%) along a channel's range where the color crosses in/out of the
// sRGB gamut — sampled finely, holding the other two channels at their current value.
// This is the "fallback" boundary: past these lines, the display falls back to the
// nearest sRGB color instead of the true OKLCH one.
const findGamutBoundaries = (current: Oklch, channel: keyof Oklch, min: number, max: number): number[] => {
    const inRgbGamut = inGamut('rgb');
    const flags = Array.from({ length: BOUNDARY_SAMPLES + 1 }, (_, i) =>
        inRgbGamut({ ...current, [channel]: min + ((max - min) * i) / BOUNDARY_SAMPLES })
    );
    const boundaries: number[] = [];
    for (let i = 1; i < flags.length; i++) {
        if (flags[i] !== flags[i - 1]) boundaries.push(((i - 0.5) / BOUNDARY_SAMPLES) * 100);
    }
    return boundaries;
};

const sliderSx = (gradient: string) => ({
    WebkitAppearance: 'none' as const,
    appearance: 'none' as const,
    width: '100%',
    height: '10px',
    borderRadius: '5px',
    background: gradient,
    outline: 'none',
    cursor: 'pointer',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        width: '16px',
        height: '16px',
        background: 'white',
        border: '2px solid black',
        borderRadius: '3px',
        transform: 'rotate(45deg)',
        cursor: 'pointer',
    },
    '&::-moz-range-track': {
        background: gradient,
        height: '10px',
        borderRadius: '5px',
    },
    '&::-moz-range-thumb': {
        width: '12px',
        height: '12px',
        background: 'white',
        border: '2px solid black',
        borderRadius: '3px',
        transform: 'rotate(45deg)',
        cursor: 'pointer',
    },
});

const ChannelSlider: React.FC<{
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    decimals: number;
    unit?: string;
    gradient: string;
    boundaries: number[];
    onChange: (value: number) => void;
}> = ({ label, value, min, max, step, decimals, unit, gradient, boundaries, onChange }) => (
    <Box sx={{ mb: 3 }}>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Text sx={{ fontSize: 0, fontWeight: 'bold' }}>{label}</Text>
            <input
                type="text"
                inputMode="decimal"
                value={value.toFixed(decimals)}
                onChange={(e) => {
                    // Plain text, not type="number" — number inputs render with the
                    // browser's locale decimal separator (e.g. "0,6" on some locales)
                    // even though the underlying value is always period-based; a text
                    // input keeps this consistently period-formatted everywhere.
                    const next = Number(e.target.value.replace(',', '.'));
                    if (!Number.isNaN(next)) onChange(Math.min(max, Math.max(min, next)));
                }}
                sx={{
                    width: '64px',
                    fontFamily: 'monospace',
                    fontSize: 0,
                    borderRadius: 'sm',
                    border: '1px solid',
                    borderColor: 'gray.4',
                    backgroundColor: 'background',
                    px: 1,
                }}
            />
        </Flex>
        <Box sx={{ position: 'relative', height: '14px' }}>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                sx={{ ...sliderSx(gradient), position: 'absolute', top: '2px', left: 0 }}
            />
            {boundaries.map((pct) => (
                <Box
                    key={pct}
                    title="sRGB gamut limit — colors past this line fall back to the nearest in-gamut color"
                    sx={{
                        position: 'absolute',
                        zIndex: 2,
                        top: 0,
                        bottom: 0,
                        left: `${pct}%`,
                        width: '2px',
                        marginLeft: '-1px',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        pointerEvents: 'none',
                    }}
                />
            ))}
        </Box>
        {unit && (
            <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                {value.toFixed(decimals)}
                {unit}
            </Text>
        )}
    </Box>
);

interface Props {
    initialColor?: string;
}

const DEFAULT_COLOR = 'oklch(70.00% 0.350 150.00)';

const toOklchString = (color: string): string => {
    const node = converter('oklch')(color);
    return node ? formatOklchString({ l: node.l, c: node.c ?? 0, h: node.h ?? 0 }) : color;
};

const ColorHelperPanel: React.FC<Props> = ({ initialColor }) => {
    const [input, setInput] = useState(() => toOklchString(initialColor || DEFAULT_COLOR));
    const [hexInput, setHexInput] = useState<string | null>(null);
    const [copiedFormat, setCopiedFormat] = useState<string | null>(null);

    const oklchNode = converter('oklch')(input);
    const hex = formatHex(input);
    const rgb = formatRgb(input);
    const valid = Boolean(oklchNode);
    const inSrgbGamut = oklchNode ? inGamut('rgb')(oklchNode) : false;

    const current: Oklch = { l: oklchNode?.l ?? 0, c: oklchNode?.c ?? 0, h: oklchNode?.h ?? 0 };

    // The hex field keeps its own draft text while being typed into (so partial input like
    // "#0" doesn't get reverted mid-keystroke); any OTHER control resets the draft so the
    // field falls back to showing the derived hex for whatever `input` now is.
    const setOklch = (next: Partial<Oklch>) => {
        setInput(formatOklchString({ ...current, ...next }));
        setHexInput(null);
    };

    const setOklchInput = (value: string) => {
        setInput(value);
        setHexInput(null);
    };

    const commitHex = (value: string) => {
        setHexInput(value);
        if (converter('oklch')(value)) setInput(value);
    };

    const copy = (format: string, text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedFormat(format);
            setTimeout(() => setCopiedFormat(null), COPIED_FEEDBACK_MS);
        });
    };

    return (
        <Box
            sx={{
                flex: '0 0 340px',
                p: 3,
                borderRadius: 'sm',
                backgroundColor: 'gray.1',
                alignSelf: 'flex-start',
                position: 'sticky',
                top: 3,
            }}
        >
            <Text sx={{ fontWeight: 'bold', mb: 2, display: 'block' }}>Color helper</Text>

            {valid && !inSrgbGamut ? (
                <Flex sx={{ height: '64px', borderRadius: 'sm', mb: 2, overflow: 'hidden', border: '1px solid', borderColor: 'gray.4' }}>
                    <Box sx={{ flex: 1, backgroundColor: input, position: 'relative' }}>
                        <Text sx={{ position: 'absolute', bottom: 1, left: 1, fontSize: 0, color: 'white', textShadow: '0 0 2px black' }}>
                            True
                        </Text>
                    </Box>
                    <Box sx={{ flex: 1, backgroundColor: hex ?? 'gray.3', position: 'relative' }}>
                        <Text sx={{ position: 'absolute', bottom: 1, left: 1, fontSize: 0, color: 'white', textShadow: '0 0 2px black' }}>
                            Fallback
                        </Text>
                    </Box>
                </Flex>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: '64px',
                        borderRadius: 'sm',
                        mb: 2,
                        backgroundColor: valid ? input : 'gray.3',
                        border: '1px solid',
                        borderColor: 'gray.4',
                    }}
                />
            )}

            <Text sx={{ fontSize: 0, color: 'gray.6', mb: 1, display: 'block' }}>OKLCH</Text>
            <Flex sx={{ gap: 2, mb: 2, alignItems: 'center' }}>
                <Input
                    value={input}
                    onChange={(e) => setOklchInput(e.target.value)}
                    placeholder="oklch(L C H)"
                    sx={{ fontFamily: 'monospace', fontSize: 0, minWidth: 0 }}
                />
                <Button
                    variant="secondary"
                    type="button"
                    onClick={() => copy('OKLCH', input)}
                    disabled={!valid}
                    sx={{ px: 2, fontSize: 0, flexShrink: 0 }}
                >
                    {copiedFormat === 'OKLCH' ? 'Copied!' : 'Copy'}
                </Button>
            </Flex>
            <Text sx={{ fontSize: 0, color: 'gray.6', mb: 1, display: 'block' }}>Hex</Text>
            <Flex sx={{ gap: 2, mb: 2, alignItems: 'center' }}>
                <Input
                    value={hexInput ?? hex ?? ''}
                    onChange={(e) => commitHex(e.target.value)}
                    placeholder="#007bff, rgb(...), hsl(...)"
                    sx={{ fontFamily: 'monospace', fontSize: 0, minWidth: 0 }}
                />
                <Button
                    variant="secondary"
                    type="button"
                    onClick={() => copy('Hex', hex ?? '')}
                    disabled={!hex}
                    sx={{ px: 2, fontSize: 0, flexShrink: 0 }}
                >
                    {copiedFormat === 'Hex' ? 'Copied!' : 'Copy'}
                </Button>
            </Flex>

            {!valid && (
                <Text color="danger" sx={{ fontSize: 0, mb: 2, display: 'block' }}>
                    Invalid color
                </Text>
            )}
            {valid && (
                <Flex sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: '10px', height: 0, borderTop: '1px dashed', borderColor: 'gray.8' }} />
                    <Text sx={{ fontSize: 0, color: 'gray.6' }}>sRGB gamut limit (past it: fallback color)</Text>
                </Flex>
            )}

            {valid && (
                <>
                    <ChannelSlider
                        label="Lightness (L)"
                        value={current.l}
                        min={0}
                        max={1}
                        step={0.001}
                        decimals={2}
                        gradient={buildGradient(current, 'l', 0, 1)}
                        boundaries={findGamutBoundaries(current, 'l', 0, 1)}
                        onChange={(l) => setOklch({ l })}
                    />
                    <ChannelSlider
                        label="Chroma (C)"
                        value={current.c}
                        min={0}
                        max={MAX_CHROMA}
                        step={0.001}
                        decimals={3}
                        gradient={buildGradient(current, 'c', 0, MAX_CHROMA)}
                        boundaries={findGamutBoundaries(current, 'c', 0, MAX_CHROMA)}
                        onChange={(c) => setOklch({ c })}
                    />
                    <ChannelSlider
                        label="Hue (H)"
                        value={current.h}
                        min={0}
                        max={360}
                        step={0.1}
                        decimals={1}
                        unit="°"
                        gradient={buildGradient(current, 'h', 0, 360)}
                        boundaries={findGamutBoundaries(current, 'h', 0, 360)}
                        onChange={(h) => setOklch({ h })}
                    />

                    {rgb && (
                        <Flex sx={{ alignItems: 'center', gap: 2, mt: 1 }}>
                            <Text sx={{ fontSize: 0, color: 'gray.6', width: '32px', flexShrink: 0 }}>RGB</Text>
                            <Text sx={{ fontFamily: 'monospace', fontSize: 1, flex: 1, wordBreak: 'break-all' }}>
                                {rgb}
                            </Text>
                            <Button variant="secondary" type="button" onClick={() => copy('RGB', rgb)}>
                                {copiedFormat === 'RGB' ? 'Copied!' : 'Copy'}
                            </Button>
                        </Flex>
                    )}
                </>
            )}

            {valid && !inSrgbGamut && (
                <Text sx={{ fontSize: 0, color: 'orange', mt: 2, display: 'block' }}>
                    Outside sRGB gamut — Hex/RGB are the sRGB fallback, not the true color
                </Text>
            )}

            <a
                href="https://culorijs.org/"
                target="_blank"
                rel="noreferrer"
                sx={{ fontSize: 0, color: 'gray.5', mt: 3, display: 'block' }}
            >
                Powered by culorijs.org
            </a>
        </Box>
    );
};

export default ColorHelperPanel;
